import { RELATIONSHIP_LABELS } from './constants';

import type { IDateOfBirthValue } from '@orn/shared-web/components/DateOfBirthInput';

export type IDependantDateOfBirth = IDateOfBirthValue;

/** Parse a `YYYY-MM-DD` ISO date back into an `IDependantDateOfBirth` triad. */
export const parseIsoToDateOfBirth = (iso: string | null): IDependantDateOfBirth | null => {
  if (!iso) {
    return null;
  }
  const match = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) {
    return null;
  }
  return { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) };
};

/** Build a `YYYY-MM-DD` ISO date string from a captured `IDependantDateOfBirth`. */
export const toIsoDate = (dob: IDependantDateOfBirth | null): string | null => {
  if (!dob) {
    return null;
  }
  const mm = String(dob.month).padStart(2, '0');
  const dd = String(dob.day).padStart(2, '0');
  return `${dob.year}-${mm}-${dd}`;
};

/**
 * Pick the dependant's display name — preferred name when set, else first
 * name. Last name is intentionally omitted across the loved-ones surface; we
 * lean on the more familiar form (preferred / first) everywhere.
 */
export const displayName = (dep: { firstName: string; preferredName?: string | null }): string => {
  const preferred = dep.preferredName?.trim();
  if (preferred) {
    return preferred;
  }
  return dep.firstName.trim();
};

// "step" is irregular in English — closed for mother/father ("Stepmother"),
// hyphenated elsewhere ("Step-grandmother") — so prefixed forms are mapped explicitly.
const STEP_LABELS: Record<string, string> = {
  MOTHER: 'Stepmother',
  FATHER: 'Stepfather',
  GRANDMOTHER: 'Step-grandmother',
  GRANDFATHER: 'Step-grandfather',
  AUNT: 'Step-aunt',
  UNCLE: 'Step-uncle',
};

/**
 * Human label for a caregiver relationship + step/in-law modifiers. For the
 * OTHER kind the free-text `relationshipOther` becomes the base and step takes
 * the hyphenated form (the closed mother/father exceptions don't apply). Accepts
 * a structural shape so both pending-form values and persisted server rows flow
 * through one helper.
 */
export const formatRelationship = (dep: {
  relationship: string | null;
  relationshipOther?: string | null;
  isStep: boolean;
  isInLaw: boolean;
}): string => {
  if (!dep.relationship) {
    return '—';
  }
  const custom = dep.relationship === 'OTHER' ? dep.relationshipOther?.trim() : undefined;
  const plain = custom
    ? `${custom[0].toUpperCase()}${custom.slice(1)}`
    : (RELATIONSHIP_LABELS[dep.relationship] ?? dep.relationship);
  const base = dep.isStep ? (STEP_LABELS[dep.relationship] ?? `Step-${plain.toLowerCase()}`) : plain;
  return dep.isInLaw ? `${base}-in-law` : base;
};
